// Container Trader: "Dealer referral program" page (/p/referral-program).
// Built entirely in code, so it does not need a matching page in Sharetribe Console.
// Positioned for dealers and established businesses as a free, risk-free way to start: they refer
// their own customers to Container Trader stock, earn a fee per delivered container, and can move
// up to Dealer Pro once they see demand. Existing partners who refer buyers keep earning under the
// same rules, but the page no longer markets the program as open to anyone.
// Links and codes come from /account/referrals, so every referral is tracked on the site.

export const REFERRAL_PAGE_ID = 'referral-program';

export const REFERRAL_FEE = '$150';

// Dealer referrals: partners earn 20% of a referred dealer's setup fee and of each monthly
// plan payment made in the dealer's first 90 days, while the dealer's plan stays active.
// Applies to Verified Seller, Dealer Growth and Dealer Pro (prices from CtSellPlans/ctSellPlansConfig.js).
// Only Dealer Pro can buy Container Trader stock, so only Dealer Pro also earns $150 per container.
export const DEALER_COMMISSION = '20%';
export const DEALER_COMMISSION_DAYS = 90;

export const REFERRAL_META = {
  title: 'Dealer Referral Program: Try Container Trader Risk-Free | Container Trader',
  description:
    'Dealers: refer your customers to Container Trader and earn $150 for every container we deliver. No monthly fee, no inventory to buy. See how it works for your business before you commit to Dealer Pro.',
};

// Synthetic page asset so PageBuilder renders this page without a Console asset.
export const referralPageData = () => ({
  meta: {
    pageTitle: { fieldType: 'metaTitle', content: REFERRAL_META.title },
    pageDescription: { fieldType: 'metaDescription', content: REFERRAL_META.description },
    socialSharing: {
      fieldType: 'openGraphData',
      title: REFERRAL_META.title,
      description: REFERRAL_META.description,
    },
  },
  sections: [
    { sectionId: 'referral-hero', sectionType: 'ctReferralHero' },
    { sectionId: 'referral-how', sectionType: 'ctReferralHow' },
    { sectionId: 'referral-who', sectionType: 'ctReferralWho' },
    { sectionId: 'referral-compare', sectionType: 'ctReferralCompare' },
    { sectionId: 'referral-dealer', sectionType: 'ctReferralDealer' },
    { sectionId: 'referral-faq', sectionType: 'ctReferralFaq' },
    { sectionId: 'referral-cta', sectionType: 'ctReferralCta' },
  ],
});

export const STEPS = [
  [
    'Get your free link and code',
    'Create a free account and get your referral link and code. There is no monthly fee, no setup fee and nothing to buy. Put the link on your website, in your emails or on a counter sign, or give your code to customers who ask about containers.',
  ],
  [
    'We handle the sale and delivery',
    'Your customer signs up with your link or code. Container Trader quotes the container and delivery from our stock, takes payment and schedules the truck. You don’t hold inventory or take on any risk.',
  ],
  [
    'You earn $150 per container',
    'Once a container from Container Trader stock is delivered to a customer who used your link or code, you earn $150 for that container, paid one time. A three-container order earns $450.',
  ],
];

// Path from referrals to Dealer Pro (shown in the "Who it's for" section).
export const UPGRADE_STEPS = [
  [
    'Start with referrals',
    'Refer your customers for free and see how many of them actually buy containers.',
  ],
  ['Watch the demand', 'Track link visits, orders and commissions on your referral dashboard.'],
  [
    'Move up to Dealer Pro when you’re ready',
    'Sell under your own name, set your own prices with Buy It Now and instant delivery quotes, buy wholesale stock and post loads on the load board.',
  ],
];

export const DEALER_STEPS = [
  [
    'Send them to our seller plans',
    'Point another equipment dealer, lumber yard, farm store or established business to our Become a dealer or Sell on Container Trader page.',
  ],
  [
    'They name you when they apply',
    'Ask them to enter your name and email in the “Referred by” field on their Verified Seller, Dealer Growth or Dealer Pro application.',
  ],
  [
    'You earn 20% for 90 days',
    'You earn 20% of their setup fee and of each monthly plan payment in their first 90 days while their plan stays active. Dealer Pro members can also buy Container Trader stock, so you earn $150 per container they buy in that time.',
  ],
];

// 20% of the setup fee and of the monthly price. Only Dealer Pro can buy Container Trader stock.
export const DEALER_EXAMPLES = [
  { plan: 'Verified Seller', monthly: '$69/mo', setupCut: '$10', monthlyCut: '$13.80' },
  { plan: 'Dealer Growth', monthly: '$199/mo', setupCut: '$20', monthlyCut: '$39.80' },
  {
    plan: 'Dealer Pro',
    monthly: '$249/mo',
    setupCut: '$20',
    monthlyCut: '$49.80',
    stockCommission: true,
  },
];

// Commission rules shown on the page (set by the owner).
export const COMMISSION_RULES = [
  'The $150 commission is paid one time per container.',
  'You earn on any purchase made within 90 days after the customer uses your referral link or code.',
  'Commissions are paid only on containers bought from Container Trader stock.',
  'Commissions are paid by Container Trader LLC net 30 days after the delivery is completed.',
  'If a customer used more than one partner’s referral link, the last link used gets the commission.',
  'No commission is paid if the customer buys from another dealer, or takes the deal off the Container Trader platform.',
];

export const WHO = [
  'Equipment and trailer dealers',
  'Lumber yards and building supply stores',
  'Farm, ranch and feed stores',
  'Shed and portable building dealers',
  'Storage and rental yards',
  'Container and storage dealers',
];

export const COMPARE = [
  {
    id: 'referral',
    tag: 'Start here, risk-free',
    title: 'Dealer referral program',
    lead: 'Refer your customers. We sell and deliver from our stock.',
    items: [
      '$150 per container delivered from Container Trader stock, paid one time',
      'No monthly fee, no setup fee and nothing to buy',
      'No inventory to hold and no delivery to arrange',
      'We quote, sell, take payment and deliver',
      'Track visits, orders and commissions on your dashboard',
    ],
  },
  {
    id: 'dealer',
    tag: 'When you’re ready',
    title: 'Dealer Pro',
    lead: 'Sell containers to your customers under your own business name.',
    items: [
      'Buy It Now listings with your own prices',
      'Instant delivery quotes using your own rates',
      'Buy wholesale stock and post loads on the load board',
      'Requires a physical business location',
      '$249/mo plus a $100 setup fee after approval',
    ],
  },
];

export const FAQ = [
  [
    'Who is the dealer referral program for?',
    'Established businesses whose customers ask about shipping containers, such as equipment dealers, lumber yards, farm and ranch stores, shed dealers and storage yards. It’s a free way to see whether containers work for your business before you commit to a Dealer Pro plan.',
  ],
  [
    'Is it really risk-free?',
    'Yes. It’s free to get your referral link and code. There is no monthly fee, no setup fee and no inventory to buy. If your customers don’t buy, you haven’t spent anything.',
  ],
  [
    'How much do I earn?',
    'You earn $150 for every container from Container Trader stock delivered to a customer who used your link or code. If a customer orders more than one container, you earn $150 for each container delivered. The $150 is paid one time per container.',
  ],
  [
    'Which purchases earn a commission?',
    'Any purchase of containers from Container Trader stock made within 90 days after the customer uses your referral link or code. If the customer buys from another dealer, or takes the deal off the Container Trader platform, no commission is paid.',
  ],
  [
    'Can customers type my code instead of using my link?',
    'Yes. Every partner gets a referral code along with their link. Customers can type it in the Referral code box when they create their Container Trader account. If they came from your link, the code is filled in for them.',
  ],
  [
    'When am I paid?',
    'Container commissions are paid net 30 days after the delivery is completed.',
  ],
  [
    'What if more than one partner refers the same customer?',
    'The last referral link the customer used before purchasing gets the commission.',
  ],
  [
    'Do I have to handle the sale or delivery?',
    'No. Once a customer signs up with your link or code, Container Trader quotes, sells and delivers the container. You don’t hold stock or take payment.',
  ],
  [
    'How do I refer a customer?',
    'Get your referral link and code, then send the link to your customer or give them your code. When they create their Container Trader account with your link or code, the referral is credited to you automatically. You can see your referrals on your referral dashboard.',
  ],
  [
    'When should I move up to Dealer Pro?',
    'When you’d rather sell containers under your own name and set your own prices. Dealer Pro adds Buy It Now listings, instant delivery quotes using your rates, wholesale buying and the load board. It requires a physical business location and costs $249 a month plus a $100 setup fee after approval.',
  ],
  [
    'Can I earn by referring another dealer?',
    'Yes. When a business you refer signs up for Verified Seller, Dealer Growth or Dealer Pro, you earn 20% of their setup fee and 20% of each monthly plan payment they make in their first 90 days. If they choose Dealer Pro, the only plan that can buy Container Trader stock, you also earn $150 per container they buy from our stock in their first 90 days. They need to enter your name and email in the “Referred by” field when they apply.',
  ],
  [
    'What if the dealer I referred cancels?',
    'You earn 20% only on payments the dealer makes while their plan is active. If they cancel within the first 90 days, their payments stop, and so do your earnings from them.',
  ],
  [
    'I already refer buyers to Container Trader. Does anything change?',
    'No. Your referral link, code and commissions keep working under the same rules.',
  ],
];
